/**
 * RoomOrientationDetector - Detects room orientation from 3D point cloud data
 *
 * This module provides functions to detect the dominant wall directions in a room
 * by analyzing point cloud data and finding vertical planes (walls).
 *
 * The detected orientation can be used to align the camera view with the room's
 * natural coordinate system.
 */

import * as THREE from 'three';
import { fitPlaneRANSAC, checkPlanarity } from './PointMathUtils';
import { getPointsAlongRay, IPointCloud } from './PointCloudMathUtils';
import { createDebugGroup, addRayToDebugGroup, addPointToDebugGroup, IViewerScene } from './DebugVisualization';
import { FullScreenLoadingWidget } from './FullScreenLoadingWidget';

export interface RoomOrientationResult {
  /** Detected room orientation angle in radians (yaw) */
  angle: number;
  /** Confidence score (0-1) */
  confidence: number;
  /** Number of wall samples used for detection */
  wallSampleCount: number;
  /** Detected wall directions (angles in radians) */
  wallDirections: number[];
  /** Wall plane inlier points (for visualization) */
  wallInliers: THREE.Vector3[];
  /** Wall plane normal vector (for visualization) */
  wallNormal: THREE.Vector3;
  /** Wall plane equation (for visualization - can be used to draw a line) */
  wallPlane: THREE.Plane;
}

export interface RoomOrientationOptions {
  /** Number of rays to cast around the center point (default: 36 = every 10°) */
  numRays?: number;
  /** Maximum distance to search for walls (default: 20m) */
  maxDistance?: number;
  /** Radius around each ray for point sampling (default: 0.5m) */
  samplingRadius?: number;
  /** Minimum verticality for wall detection (0-1, default: 0.8 = ~37° from vertical) */
  minVerticality?: number;
  /** RANSAC inlier threshold for wall plane fitting (default: 0.1m) */
  ransacThreshold?: number;
  /** Angle tolerance for grouping similar wall directions (default: 15°) */
  angleGroupingTolerance?: number;
  /** Number of vertical levels to sample (default: 5) */
  verticalLevels?: number;
  /** Vertical spacing between levels in meters (default: 0.3m) */
  verticalSpacing?: number;
  /** Proximity clustering radius for filtering nearby points (default: 0.3m) */
  proximityClusterRadius?: number;
  /** Minimum cluster size for valid point groups (default: 3) */
  minClusterSize?: number;
  /** Z height for ray casting. If provided, overrides centerPoint.z */
  rayZ?: number;
  /** Viewer instance for debug visualization (optional) */
  viewer?: IViewerScene;
  /** Translates the user-facing loading message (defaults to identity) */
  translate?: (source: string) => string;
}

/**
 * Small delay to allow UI to render
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Detect room orientation from a point cloud by finding dominant wall directions
 *
 * This function:
 * 1. Casts horizontal rays from a center point in all directions
 * 2. Detects vertical wall planes using RANSAC
 * 3. Analyzes the distribution of wall angles
 * 4. Returns the dominant wall direction as the room orientation
 *
 * @param pointcloud - The point cloud to analyze
 * @param centerPoint - Center point in the room
 * @param options - Detection options
 * @returns Room orientation result, or null if detection failed
 */
export async function detectRoomOrientationFromPointCloud(pointcloud: IPointCloud, centerPoint: THREE.Vector3, options: RoomOrientationOptions = {}): Promise<RoomOrientationResult | null> {
  const {
    numRays = 36,
    maxDistance = 20,
    samplingRadius = 0.5,
    minVerticality = 0.8,
    ransacThreshold = 0.03, // Strict threshold (3cm) to reduce outlier influence
    angleGroupingTolerance = (15 * Math.PI) / 180,
    verticalLevels = 5,
    verticalSpacing = 0.3,
    proximityClusterRadius = 0.3,
    minClusterSize = 3,
    viewer,
  } = options;
  const t = options.translate ?? ((source: string): string => source);

  const wallAngles: Array<{ angle: number; confidence: number }> = [];

  // Show loading widget during detection
  const loadingWidget = new FullScreenLoadingWidget(false);
  loadingWidget.show(t('Aligning screen …'));

  // Small delay to allow UI to render the loading widget
  await delay(0);

  // Create debug groups if viewer provided
  let raysGroup: THREE.Group | null = null;
  let rawPointsGroup: THREE.Group | null = null;
  let filteredPointsGroup: THREE.Group | null = null;
  let wallPointsGroup: THREE.Group | null = null;

  if (viewer) {
    raysGroup = createDebugGroup(viewer, 'room-orientation-rays-debug');
    rawPointsGroup = createDebugGroup(viewer, 'room-orientation-raw-points-debug');
    filteredPointsGroup = createDebugGroup(viewer, 'room-orientation-filtered-points-debug');
    wallPointsGroup = createDebugGroup(viewer, 'room-orientation-wall-points-debug');
  }

  // Collect ALL planar center points from ALL rays first
  // Each point also stores its ray angle for later analysis
  const allWallCenterPoints: Array<{ point: THREE.Vector3; rayAngle: number }> = [];

  // Cast rays in 4 alternating quadrants (skip every other quadrant)
  // This prevents rays from opposite sides of a wall from creating one giant cluster
  // Quadrants: 0-45°, 90-135°, 180-225°, 270-315° (like a "+" rotated 22.5°)
  const quadrantSize = Math.PI / 4; // 45 degrees per quadrant
  const isInActiveQuadrant = (angle: number): boolean => {
    // Normalize angle to [0, 2π)
    const normalized = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    // Check which octant (0-7) and only allow even octants (0, 2, 4, 6)
    const octant = Math.floor(normalized / quadrantSize);
    return octant % 2 === 0;
  };

  // Cast rays in alternating quadrants, at multiple vertical levels
  for (let i = 0; i < numRays; i++) {
    const angle = (i / numRays) * 2 * Math.PI;

    // Skip rays in inactive quadrants
    if (!isInActiveQuadrant(angle)) continue;

    const direction = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);

    for (let level = 0; level < verticalLevels; level++) {
      // Calculate Z offset: centered around the center point
      const zOffset = (level - (verticalLevels - 1) / 2) * verticalSpacing;
      const rayOrigin = centerPoint.clone();
      rayOrigin.z += zOffset;

      // Get points along this ray
      const ray = new THREE.Ray(rayOrigin, direction);
      const pointsWithSegments = getPointsAlongRay(pointcloud, ray, maxDistance, samplingRadius, true);

      // Extract positions from PointWithSegment objects
      const points = pointsWithSegments.map((p) => p.position);

      // Add ray to debug visualization
      if (raysGroup) {
        addRayToDebugGroup(raysGroup, rayOrigin, direction, maxDistance, points.length > 0);
      }

      // Add raw points to debug
      if (rawPointsGroup) {
        for (const point of points) {
          addPointToDebugGroup(rawPointsGroup, point, { color: 0xff0000, size: 0.03, opacity: 0.4 });
        }
      }

      // Check if points form a planar surface (wall)
      // For small point clusters from ray hits, we only check flatness
      // (spread requirements don't apply to tight clusters)
      if (points.length >= 3) {
        const planarityResult = checkPlanarity(points, {
          planarThreshold: 0.1, // 10cm std dev for wall detection
          minPerpendicularSpread: 0, // No spread requirement for small clusters
          minSpreadRatio: 0, // No ratio requirement for small clusters
        });

        if (planarityResult.isPlanar) {
          allWallCenterPoints.push({ point: planarityResult.center, rayAngle: angle });

          // Add filtered (planar center) points to debug
          if (filteredPointsGroup) {
            addPointToDebugGroup(filteredPointsGroup, planarityResult.center, { color: 0xffff00, size: 0.04, opacity: 0.6 });
          }
        }
      }
    }
  }

  // Now apply proximity clustering across ALL collected center points
  // This clusters points from neighboring rays that hit the same wall
  const clusters = clusterPointsByProximity(
    allWallCenterPoints.map((p) => p.point),
    proximityClusterRadius,
    minClusterSize,
  );

  // Process each cluster to detect wall planes and track the biggest one
  interface ClusterResult {
    inliers: THREE.Vector3[];
    normal: THREE.Vector3;
    confidence: number;
    angle: number;
  }
  let biggestCluster: ClusterResult | null = null;

  // Try with strict requirements first, then relax if nothing found
  const thresholdMultipliers = [1.0, 2.0, 3.0]; // Progressively relax threshold
  const verticalityMultipliers = [1.0, 0.75, 0.5]; // Progressively relax verticality requirement

  for (let attempt = 0; attempt < thresholdMultipliers.length && !biggestCluster; attempt++) {
    const currentThreshold = ransacThreshold * thresholdMultipliers[attempt];
    const currentMinVerticality = minVerticality * verticalityMultipliers[attempt];

    for (const cluster of clusters) {
      if (cluster.length < 3) continue; // Need at least 3 points for plane fitting

      // First pass: fit a vertical plane to this cluster with RANSAC
      const planeResult = fitPlaneRANSAC(cluster, {
        iterations: 200,
        inlierThreshold: currentThreshold,
        minPoints: 3,
      });

      if (!planeResult) continue;

      // Check if the plane is vertical (normal should be horizontal)
      const normal = planeResult.normal;
      const verticality = 1 - Math.abs(normal.z); // 1 = perfectly vertical wall

      if (verticality < currentMinVerticality) continue;

      // Calculate wall direction from the normal (perpendicular to normal in XY plane)
      // The wall direction is the angle of the normal projected onto XY plane
      const wallAngle = Math.atan2(normal.y, normal.x);

      // Normalize angle to [0, π) since walls have 180° symmetry
      let normalizedAngle = wallAngle;
      if (normalizedAngle < 0) normalizedAngle += Math.PI;
      if (normalizedAngle >= Math.PI) normalizedAngle -= Math.PI;

      // Track this wall for angle grouping (used for perpendicular wall check)
      wallAngles.push({
        angle: normalizedAngle,
        confidence: planeResult.confidence,
      });

      // Check if this is the biggest cluster (by inlier count)
      if (!biggestCluster || planeResult.inliers.length > biggestCluster.inliers.length) {
        biggestCluster = {
          inliers: planeResult.inliers,
          normal: planeResult.normal,
          confidence: planeResult.confidence,
          angle: normalizedAngle,
        };
      }
    }
  }

  // Use the biggest cluster's angle as the room orientation
  if (!biggestCluster) {
    loadingWidget.hide();
    loadingWidget.dispose();
    return null;
  }

  // Second pass: refine the biggest cluster with another RANSAC pass on just the inliers
  // This helps remove any remaining outliers with a much stricter threshold
  if (biggestCluster.inliers.length >= 5) {
    const refinedResult = fitPlaneRANSAC(biggestCluster.inliers, {
      iterations: 500, // Many iterations for thorough refinement
      inlierThreshold: ransacThreshold * 0.3, // Much tighter threshold (30% of original)
      minPoints: 3,
    });

    if (refinedResult) {
      const verticality = 1 - Math.abs(refinedResult.normal.z);
      if (verticality >= minVerticality) {
        // Recalculate angle from refined normal
        const wallAngle = Math.atan2(refinedResult.normal.y, refinedResult.normal.x);
        let normalizedAngle = wallAngle;
        if (normalizedAngle < 0) normalizedAngle += Math.PI;
        if (normalizedAngle >= Math.PI) normalizedAngle -= Math.PI;

        biggestCluster = {
          inliers: refinedResult.inliers,
          normal: refinedResult.normal,
          confidence: refinedResult.confidence,
          angle: normalizedAngle,
        };
      }
    }
  }

  // Add wall inlier points to debug (green = successful wall detection)
  if (wallPointsGroup) {
    // Clear previous points
    while (wallPointsGroup.children.length > 0) {
      wallPointsGroup.remove(wallPointsGroup.children[0]);
    }
    for (const point of biggestCluster.inliers) {
      addPointToDebugGroup(wallPointsGroup, point, { color: 0x00ff00, size: 0.05, opacity: 0.8 });
    }
  }

  // Group all wall angles to check for perpendicular walls (helps with confidence)
  const angleGroups = groupWallAngles(wallAngles, angleGroupingTolerance);

  // Check if we have perpendicular walls (indicating a rectangular room)
  let hasPerpendicularWalls = false;
  if (angleGroups.length >= 2) {
    const sortedGroups = angleGroups.sort((a, b) => b.totalConfidence - a.totalConfidence);
    const angleDiff = Math.abs(sortedGroups[0].representativeAngle - sortedGroups[1].representativeAngle);
    const normalizedDiff = Math.min(angleDiff, Math.PI - angleDiff);
    hasPerpendicularWalls = Math.abs(normalizedDiff - Math.PI / 2) < angleGroupingTolerance;
  }

  // Calculate confidence based on biggest cluster and perpendicular wall detection
  const confidence = hasPerpendicularWalls ? 0.9 : biggestCluster.confidence;

  // Hide loading widget
  loadingWidget.hide();
  loadingWidget.dispose();

  // Create a THREE.Plane from the normal and a point on the plane (center of inliers)
  const planeCenter = new THREE.Vector3();
  for (const point of biggestCluster.inliers) {
    planeCenter.add(point);
  }
  planeCenter.divideScalar(biggestCluster.inliers.length);
  const wallPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(biggestCluster.normal.clone().normalize(), planeCenter);

  return {
    angle: biggestCluster.angle,
    confidence,
    wallSampleCount: wallAngles.length,
    wallDirections: angleGroups.map((g) => g.representativeAngle),
    wallInliers: biggestCluster.inliers,
    wallNormal: biggestCluster.normal,
    wallPlane,
  };
}

/**
 * Cluster points by proximity using flood-fill algorithm
 * Returns all clusters that meet the minimum size requirement
 *
 * @param points - Input points
 * @param radius - Clustering radius (points within this distance are considered neighbors)
 * @param minClusterSize - Minimum number of points in a valid cluster
 * @returns Array of clusters, each cluster is an array of points
 */
function clusterPointsByProximity(points: THREE.Vector3[], radius: number, minClusterSize: number): THREE.Vector3[][] {
  if (points.length < minClusterSize) {
    return [];
  }

  const radiusSquared = radius * radius;
  const visited = new Set<number>();
  const clusters: THREE.Vector3[][] = [];

  // For each point, find its cluster using flood fill
  for (let i = 0; i < points.length; i++) {
    if (visited.has(i)) continue;

    // Find all points in this cluster using flood fill
    const clusterIndices: number[] = [];
    const queue: number[] = [i];

    while (queue.length > 0) {
      const current = queue.pop();
      if (current === undefined) break;
      if (visited.has(current)) continue;

      visited.add(current);
      clusterIndices.push(current);

      const currentPoint = points[current];

      // Find neighbors
      for (let j = 0; j < points.length; j++) {
        if (visited.has(j)) continue;

        const dx = points[j].x - currentPoint.x;
        const dy = points[j].y - currentPoint.y;
        const dz = points[j].z - currentPoint.z;
        const distSquared = dx * dx + dy * dy + dz * dz;

        if (distSquared <= radiusSquared) {
          queue.push(j);
        }
      }
    }

    // If cluster is large enough, add it to the result
    if (clusterIndices.length >= minClusterSize) {
      const cluster = clusterIndices.map((idx) => points[idx]);
      clusters.push(cluster);
    }
  }

  return clusters;
}

interface AngleGroup {
  representativeAngle: number;
  totalConfidence: number;
  count: number;
}

/**
 * Group similar wall angles together
 */
function groupWallAngles(wallAngles: Array<{ angle: number; confidence: number }>, tolerance: number): AngleGroup[] {
  const groups: AngleGroup[] = [];

  for (const wall of wallAngles) {
    let foundGroup = false;

    for (const group of groups) {
      const angleDiff = Math.abs(wall.angle - group.representativeAngle);
      // Handle wraparound at π
      const normalizedDiff = Math.min(angleDiff, Math.PI - angleDiff);

      if (normalizedDiff <= tolerance) {
        // Add to existing group
        const newTotal = group.totalConfidence + wall.confidence;
        // Weighted average of angles
        group.representativeAngle = (group.representativeAngle * group.totalConfidence + wall.angle * wall.confidence) / newTotal;
        group.totalConfidence = newTotal;
        group.count++;
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      groups.push({
        representativeAngle: wall.angle,
        totalConfidence: wall.confidence,
        count: 1,
      });
    }
  }

  return groups;
}

/**
 * Detect room orientation from room polygon vertices
 *
 * This is a simpler method that works when room polygons are already available.
 * It analyzes the edge directions of the polygon to find the dominant orientation.
 *
 * @param polygon - Array of 3D points defining the room boundary
 * @returns Room orientation angle in radians, or null if detection failed
 */
export function detectRoomOrientationFromPolygon(polygon: THREE.Vector3[]): number | null {
  if (polygon.length < 3) return null;

  // Calculate edge angles
  const edges: Array<{ angle: number; length: number }> = [];

  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < 0.01) continue; // Skip very short edges

    let angle = Math.atan2(dy, dx);

    // Normalize to [0, π) since edges have 180° symmetry
    if (angle < 0) angle += Math.PI;
    if (angle >= Math.PI) angle -= Math.PI;

    edges.push({ angle, length });
  }

  if (edges.length === 0) return null;

  // Create histogram of angles weighted by edge length
  const numBins = 36; // 5° per bin
  const binSize = Math.PI / numBins;
  const histogram: number[] = new Array(numBins).fill(0);

  for (const edge of edges) {
    const binIndex = Math.min(Math.floor(edge.angle / binSize), numBins - 1);
    histogram[binIndex] += edge.length;
  }

  // Find peaks in histogram (looking for 2 perpendicular peaks for rectangular rooms)
  const peaks: Array<{ binIndex: number; weight: number; angle: number }> = [];
  const minPeakHeight = Math.max(...histogram) * 0.2;

  for (let i = 0; i < numBins; i++) {
    const prev = histogram[(i - 1 + numBins) % numBins];
    const curr = histogram[i];
    const next = histogram[(i + 1) % numBins];

    if (curr > prev && curr > next && curr > minPeakHeight) {
      peaks.push({
        binIndex: i,
        weight: curr,
        angle: (i + 0.5) * binSize,
      });
    }
  }

  if (peaks.length === 0) {
    // Fallback: use the bin with highest weight
    const maxBin = histogram.indexOf(Math.max(...histogram));
    return (maxBin + 0.5) * binSize;
  }

  // Sort peaks by weight and return the strongest direction
  peaks.sort((a, b) => b.weight - a.weight);
  return peaks[0].angle;
}

/**
 * Calculate minimum area bounding rectangle for a polygon
 * Uses rotating calipers algorithm
 *
 * @param polygon - Array of 2D points (x, y)
 * @returns Bounding rectangle info with angle
 */
export function minAreaBoundingRect(polygon: Array<{ x: number; y: number }>): {
  angle: number;
  width: number;
  height: number;
  center: { x: number; y: number };
} | null {
  if (polygon.length < 3) return null;

  // Compute convex hull first
  const hull = convexHull(polygon);
  if (hull.length < 3) return null;

  let minArea = Infinity;
  let bestAngle = 0;
  let bestWidth = 0;
  let bestHeight = 0;
  let bestCenter = { x: 0, y: 0 };

  // Try each edge of the hull as a potential side
  for (let i = 0; i < hull.length; i++) {
    const p1 = hull[i];
    const p2 = hull[(i + 1) % hull.length];

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const edgeAngle = Math.atan2(dy, dx);

    // Rotate all points so edge aligns with x-axis
    const cos = Math.cos(-edgeAngle);
    const sin = Math.sin(-edgeAngle);

    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    for (const p of hull) {
      const rx = p.x * cos - p.y * sin;
      const ry = p.x * sin + p.y * cos;

      minX = Math.min(minX, rx);
      maxX = Math.max(maxX, rx);
      minY = Math.min(minY, ry);
      maxY = Math.max(maxY, ry);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const area = width * height;

    if (area < minArea) {
      minArea = area;
      bestAngle = edgeAngle;
      bestWidth = width;
      bestHeight = height;

      // Calculate center in original coordinates
      const centerRotated = {
        x: (minX + maxX) / 2,
        y: (minY + maxY) / 2,
      };
      bestCenter = {
        x: centerRotated.x * Math.cos(edgeAngle) - centerRotated.y * Math.sin(edgeAngle),
        y: centerRotated.x * Math.sin(edgeAngle) + centerRotated.y * Math.cos(edgeAngle),
      };
    }
  }

  // Normalize angle to use the longer side direction
  let angle = bestAngle;
  if (bestWidth < bestHeight) {
    angle += Math.PI / 2;
  }

  // Normalize to [-π, π]
  angle = Math.atan2(Math.sin(angle), Math.cos(angle));

  return { angle, width: bestWidth, height: bestHeight, center: bestCenter };
}

/**
 * Compute convex hull using Graham scan
 */
function convexHull(points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
  if (points.length < 3) return [...points];

  // Find bottom-left point
  let startIdx = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].y < points[startIdx].y || (points[i].y === points[startIdx].y && points[i].x < points[startIdx].x)) {
      startIdx = i;
    }
  }

  const start = points[startIdx];

  // Sort points by polar angle relative to start
  const sorted = points
    .filter((_, i) => i !== startIdx)
    .map((p) => ({
      point: p,
      angle: Math.atan2(p.y - start.y, p.x - start.x),
      dist: Math.hypot(p.x - start.x, p.y - start.y),
    }))
    .sort((a, b) => a.angle - b.angle || a.dist - b.dist)
    .map((p) => p.point);

  // Graham scan
  const hull: Array<{ x: number; y: number }> = [start];

  for (const p of sorted) {
    while (hull.length >= 2 && cross(hull[hull.length - 2], hull[hull.length - 1], p) <= 0) {
      hull.pop();
    }
    hull.push(p);
  }

  return hull;
}

/**
 * Cross product of vectors (p1->p2) and (p1->p3)
 */
function cross(p1: { x: number; y: number }, p2: { x: number; y: number }, p3: { x: number; y: number }): number {
  return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
}
